import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function fmtDate(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function TicketPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | notfound
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/order-messages/ticket/${token}`);
      setData(r.data);
      setState('ready');
    } catch (e) {
      setState('notfound');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [data]);

  const send = async () => {
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    // Optimistic append
    setData((prev) => prev ? {
      ...prev,
      messages: [...prev.messages, { text, is_agent: false, author: prev.customer_name || 'Tú', date: new Date().toISOString() }],
    } : prev);
    setReply('');
    try {
      await axios.post(`${BACKEND_URL}/api/order-messages/ticket/${token}/reply`, { reply: text });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (e) {
      // revert not critical; show error
    } finally {
      setSending(false);
    }
  };

  const primary = data?.primary_color || '#6366f1';

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={32} color="#64748b" data-testid="ticket-loading" />
      </div>
    );
  }

  if (state === 'notfound') {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 420, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,.06)' }} data-testid="ticket-notfound">
          <AlertCircle size={40} color="#ef4444" style={{ margin: '0 auto 16px' }} />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Ticket no encontrado</h1>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Este enlace no es válido o el ticket ya no está disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, -apple-system, sans-serif' }} data-testid="ticket-page">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px' }}>
        {/* Header */}
        <div style={{ padding: '28px 0 20px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }} data-testid="ticket-store-name">{data.store_name}</div>
          <div style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
            {data.order_id && data.order_id !== '0' ? `Ticket del pedido #${data.order_id}` : 'Tu conversación de soporte'}
          </div>
        </div>

        {/* Conversation */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 2px 16px rgba(0,0,0,.05)', marginBottom: 16 }} data-testid="ticket-conversation">
          {data.messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.is_agent ? 'flex-start' : 'flex-end', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4, padding: '0 4px' }}>
                {m.author} · {fmtDate(m.date)}
              </div>
              <div style={{
                maxWidth: '82%',
                padding: '10px 14px',
                borderRadius: 14,
                fontSize: 15,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                background: m.is_agent ? '#f1f5f9' : primary,
                color: m.is_agent ? '#1e293b' : '#fff',
                borderBottomLeftRadius: m.is_agent ? 4 : 14,
                borderBottomRightRadius: m.is_agent ? 14 : 4,
              }}>
                {m.text}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {/* Reply box */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 2px 16px rgba(0,0,0,.05)', marginBottom: 40, position: 'sticky', bottom: 16 }}>
          {sent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#16a34a', fontSize: 13, marginBottom: 10 }} data-testid="ticket-sent-ok">
              <CheckCircle2 size={16} /> Mensaje enviado. El equipo te responderá pronto.
            </div>
          )}
          <textarea
            data-testid="ticket-reply-input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder="Escribe tu respuesta..."
            rows={3}
            style={{
              width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12,
              fontSize: 15, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              background: '#fff', color: '#0f172a',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button
              data-testid="ticket-send-btn"
              onClick={send}
              disabled={sending || !reply.trim()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: primary, color: '#fff', border: 'none', borderRadius: 10,
                padding: '10px 20px', fontSize: 14, fontWeight: 600,
                cursor: sending || !reply.trim() ? 'default' : 'pointer',
                opacity: sending || !reply.trim() ? 0.6 : 1,
              }}
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
